import { useState, useEffect } from 'react';

/**
 * Custom React hook to detect if the user is on a mobile device.
 * It checks both the user agent string and the window width.
 *
 * @returns {boolean} True if the device is mobile, false otherwise.
 */
export function useIsMobile() {
    const checkMobile = () => {
        if (typeof window === 'undefined' || typeof navigator === 'undefined') {
            return false;
        }
        
        const ua = navigator.userAgent || '';
        
        const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS|FxiOS/i;
        
        if (mobileRegex.test(ua)) {
            return true;
        }
        
        const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
        const isSmallScreen = window.innerWidth < 768;
        
        if (hasTouch && isSmallScreen) {
            return true;
        }
        
        return window.innerWidth < 600;
    };

    const [isMobile, setIsMobile] = useState(() => checkMobile());

    useEffect(() => {
        let rafId = null;

        const updateMobileState = () => {
            if (rafId !== null) {
                cancelAnimationFrame(rafId);
            }

            rafId = requestAnimationFrame(() => {
                setIsMobile(checkMobile());
            });
        };
        
        updateMobileState();
        window.addEventListener('resize', updateMobileState);
        window.addEventListener('orientationchange', updateMobileState);
        
        return () => {
            window.removeEventListener('resize', updateMobileState);
            window.removeEventListener('orientationchange', updateMobileState);
            if (rafId !== null) {
                cancelAnimationFrame(rafId);
            }
        };
    }, []);

    return isMobile;
}